import React, { useEffect, useState } from 'react';
import { LABEL_COLORS, LABEL_ICONS, createLabel } from '../../utils/labelApi';
import { LabelIcon } from './LabelIcons';
import './labels.css';

const CreateLabelModal = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState(LABEL_COLORS[0]);
  const [icon, setIcon] = useState(LABEL_ICONS[0]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setColor(LABEL_COLORS[0]); 
    setIcon(LABEL_ICONS[0]);
    setError('');
    setSaving(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Label name is required');
      return;
    }
    if (trimmed.length > 30) {
      setError('Label name cannot exceed 30 characters');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await createLabel({ name: trimmed, color, icon });
      if (response.success) {
        onCreated?.(response.data);
        onClose?.();
      } else {
        setError(response.message || 'Could not create label');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create label');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="label-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="label-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-label-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="create-label-title">Create label</h2>
        <label className="label-modal-field">
          <span>Label name</span>
          <input
            type="text"
            value={name}
            maxLength={30}
            autoFocus
            placeholder="e.g. Office"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
              if (event.key === 'Escape') onClose?.();
            }}
          />
        </label>
        <div className="label-modal-field">
          <span>Color</span>
          <div className="label-color-grid">
            {LABEL_COLORS.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={`label-color-swatch${color === swatch ? ' is-selected' : ''}`}
                style={{ background: swatch }}
                aria-label={`Color ${swatch}`}
                onClick={() => setColor(swatch)}
              />
            ))}
          </div>
        </div>
        <div className="label-modal-field">
          <span>Icon</span>
          <div className="label-icon-grid">
            {LABEL_ICONS.map((iconName) => (
              <button
                key={iconName}
                type="button"
                className={`label-icon-swatch${icon === iconName ? ' is-selected' : ''}`}
                aria-label={`Icon ${iconName}`}
                onClick={() => setIcon(iconName)}
              >
                <LabelIcon name={iconName} size={18} color={color} />
              </button>
            ))}
          </div>
        </div>
        {error && <div className="label-modal-error">{error}</div>}
        <div className="label-modal-actions">
          <button type="button" className="label-modal-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="label-modal-save" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLabelModal;
